import { useEffect, useRef, useState } from "react";
import { RotateCw, X } from "lucide-react";

// NFC review card — clickable thumbnail opens a modal with a spinnable 3D card.
// Front = real card render (/nfc-front.jpg), back = real QR-code back
// (/nfc-back.jpg), plus a "Set" view with the two-card lifestyle shot
// (/nfc-set.jpg). Drag to spin 360°. Pure CSS 3D — no WebGL.
export function NfcCard3D({ imgClassName = "h-14 w-14", testId = "nfc-card", fit = "cover" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid={`${testId}-open`}
        title="See the card — spin it in 3D"
        className="group relative z-20 shrink-0 cursor-pointer transition-transform duration-200 hover:-translate-y-0.5 hover:scale-[1.04]">
        <img src="/nfc-front.jpg" alt="NFC Google review card"
          className={`${imgClassName} ${fit === "contain" ? "rounded-lg object-contain" : "rounded-xl object-cover"} shadow-sm ring-1 ring-navy/10`} />
        <span className="absolute -bottom-1.5 -right-1.5 grid h-5 w-5 place-items-center rounded-full bg-brand-gradient text-white shadow-pill transition-transform duration-200 group-hover:scale-110">
          <RotateCw className="h-3 w-3" />
        </span>
      </button>
      {open && <Card3DModal onClose={() => setOpen(false)} testId={testId} />}
    </>
  );
}

function Card3DModal({ onClose, testId }) {
  const [rot, setRot] = useState({ x: -6, y: -18 });
  const [dragging, setDragging] = useState(false);
  const [idle, setIdle] = useState(true); // gentle sway until first touch
  const [view, setView] = useState("card"); // card (3D front/back) | set (lifestyle pair)
  const drag = useRef(null);

  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", esc);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden"; // no page scroll under the card on phones
    return () => { window.removeEventListener("keydown", esc); document.body.style.overflow = prev; };
  }, [onClose]);

  // Drag anywhere (window-level listeners while the pointer is down) so a finger
  // sliding off the card on a phone keeps spinning it; a plain tap flips it.
  const rotRef = useRef(rot);
  rotRef.current = rot;
  useEffect(() => {
    if (!dragging) return undefined;
    const move = (e) => {
      const d = drag.current;
      if (!d) return;
      d.moved = d.moved || Math.abs(e.clientX - d.px) + Math.abs(e.clientY - d.py) > 6;
      setRot({
        x: Math.max(-35, Math.min(35, d.x - (e.clientY - d.py) * 0.35)),
        y: d.y + (e.clientX - d.px) * 0.55,
      });
    };
    const up = () => {
      const d = drag.current;
      if (d && !d.moved) setRot((r) => ({ x: -6, y: r.y + 180 })); // tap = flip
      setDragging(false);
      drag.current = null;
    };
    window.addEventListener("pointermove", move, { passive: true });
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
    };
  }, [dragging]);
  const start = (e) => {
    if (view !== "card") return;
    e.preventDefault();
    setIdle(false);
    setDragging(true);
    drag.current = { px: e.clientX, py: e.clientY, moved: false, ...rotRef.current };
  };
  const flipTo = (y) => { setIdle(false); setView("card"); setRot({ x: -6, y }); };
  const yNorm = ((rot.y % 360) + 360) % 360;
  const showingBack = view === "card" && yNorm > 90 && yNorm < 270;

  return (
    // close on a genuine backdrop PRESS only — a card drag that ends on the
    // backdrop fires click on the common ancestor and must NOT close the modal
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-navy/70 p-6 backdrop-blur-sm"
      onPointerDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid={`${testId}-modal`}>
      <div className="relative flex w-full max-w-sm flex-col items-center" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} data-testid={`${testId}-close`}
          className="absolute -top-2 right-0 z-10 grid h-9 w-9 place-items-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20">
          <X className="h-4 w-4" />
        </button>
        <p className="mb-6 px-12 text-center font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-white/60">
          {view === "set" ? "The set we mail you · $30 value" : "Your review card · $30 value"}
          <span className="mt-1 block tracking-[0.18em] text-white/40">drag to spin · tap to flip</span>
        </p>

        {view === "set" ? (
          <img src="/nfc-set.jpg" alt="The NFC review card set — tap card and QR card"
            data-testid={`${testId}-set`} className="w-full rounded-2xl shadow-2xl ring-1 ring-white/20" draggable={false} />
        ) : (
          /* 3D scene */
          <div
            className="cursor-grab touch-none select-none active:cursor-grabbing"
            style={{ perspective: "1100px", touchAction: "none" }}
            onPointerDown={start}>
            <div data-testid={`${testId}-3d`}
              className={idle ? "nfc-idle" : ""}
              style={{
                width: 230, height: 368, position: "relative", transformStyle: "preserve-3d",
                transform: `rotateX(${rot.x}deg) rotateY(${rot.y}deg)`,
                transition: dragging ? "none" : "transform .5s cubic-bezier(.22,.9,.3,1)",
              }}>
              {/* front — real card render */}
              <div className="absolute inset-0 overflow-hidden rounded-2xl shadow-2xl ring-1 ring-white/20" style={{ backfaceVisibility: "hidden" }}>
                <img src="/nfc-front.jpg" alt="Front of the NFC review card — tap to leave a Google review" className="h-full w-full object-cover" draggable={false} />
              </div>
              {/* back — real QR-code render */}
              <div className="absolute inset-0 overflow-hidden rounded-2xl shadow-2xl ring-1 ring-white/20"
                style={{ backfaceVisibility: "hidden", transform: "rotateY(180deg)" }}>
                <img src="/nfc-back.jpg" alt="Back of the card — QR code to the Google review page" className="h-full w-full object-cover" draggable={false} />
              </div>
            </div>
          </div>
        )}

        {/* controls */}
        <div className="mt-7 flex items-center gap-2">
          <button onClick={() => flipTo(0)} data-testid={`${testId}-front-btn`}
            className={`rounded-full px-4 py-1.5 text-[12px] font-bold transition-colors ${view === "card" && !showingBack ? "bg-white text-navy" : "bg-white/10 text-white/70 hover:bg-white/20"}`}>Front</button>
          <button onClick={() => flipTo(180)} data-testid={`${testId}-back-btn`}
            className={`rounded-full px-4 py-1.5 text-[12px] font-bold transition-colors ${showingBack ? "bg-white text-navy" : "bg-white/10 text-white/70 hover:bg-white/20"}`}>Back (QR)</button>
          <button onClick={() => setView("set")} data-testid={`${testId}-set-btn`}
            className={`rounded-full px-4 py-1.5 text-[12px] font-bold transition-colors ${view === "set" ? "bg-white text-navy" : "bg-white/10 text-white/70 hover:bg-white/20"}`}>The set</button>
        </div>
      </div>
    </div>
  );
}
